'use strict';

angular.module('utils').factory('OCAppointmentUtil', function(OpencareConstants, AvailabilityConstants, OCStringUtil, DoctorService, $q) {
  const { actionTypes } = OpencareConstants.appointmentHistory;

  var self = {

    isRequested: function(status) {
      return status == OpencareConstants.appointmentStates.REQUESTED;
    },

    isScheduled: function(status) {
      return _.includes([
        OpencareConstants.appointmentStates.SCHEDULED,
        OpencareConstants.appointmentStates.CONFIRMED,
      ], status);
    },

    isCompleted: function(status) {
      return status == OpencareConstants.appointmentStates.COMPLETED;
    },

    isArchived: function(status) {
      return status == OpencareConstants.appointmentStates.ARCHIVED;
    },

    isReconcilable: function(appointment) {
      return self.isRequested(appointment.status) || self.isScheduled(appointment.status);
    },

    needsVerification: (appointment) =>
      Boolean(self.isCompleted(appointment.status) && !appointment.verified),

    isRescheduled: function(status, includeScheduledByClinic) {
      var statuses = [OpencareConstants.appointmentStates.RESCHEDULED];
      if (includeScheduledByClinic) {
        statuses.push(OpencareConstants.appointmentStates.SCHEDULED_BY_CLINIC);
      }
      return _.includes(statuses, status);
    },

    isRescheduleSlotOptionItem: function(historyItem) {
      return historyItem.actionType == actionTypes.PROVIDER_ADDED_RESCHEDULE_SLOTS
        && historyItem.actor == OpencareConstants.appointmentHistory.actors.provider;
    },

    isRescheduleSlotOptionResponseItem: function(historyItem) {
      return historyItem.actionType == actionTypes.PATIENT_RESPOND_RESCHEDULE_SLOTS
        && historyItem.actor == OpencareConstants.appointmentHistory.actors.patient;
    },

    isRescheduledByPatient: function(historyItem) {
      return historyItem.actionType == actionTypes.STATUS
        && historyItem.fromStatus == OpencareConstants.appointmentStates.CANCELLED
        && historyItem.toStatus == OpencareConstants.appointmentStates.REQUESTED
        && historyItem.actor == OpencareConstants.appointmentHistory.actors.patient;
    },

    isCancelledByClinic: function(status) {
      return _.includes([
        OpencareConstants.appointmentStates.CANCELLED,
        OpencareConstants.appointmentStates.CANCELLED_BY_CLINIC,
      ], status);
    },

    isCancelled: function(status) {
      return self.isCancelledByClinic(status) || status == OpencareConstants.appointmentStates.CANCELLED_BY_PATIENT;
    },

    isInPast: function(appointment) {
      return appointment.date && !_.isNull(appointment.startTime) && moment(appointment.date, 'YYYY-MM-DD').startOf('day')
        .add(appointment.startTime, 'minutes')
        .add(appointment.duration, 'minutes')
        .diff(moment()) < 0;
    },

    isRequestedAndReserved: function(appointment) {
      return !!(self.isRequested(appointment.status) && _.get(appointment, 'reservedAppointmentSlot.status') == OpencareConstants.reservedAppointmentSlotStatus.REQUESTED);
    },

    isBilled: function(appointment) {
      var billingStatus = _.get(appointment, 'metadata.billingStatus');
      return _.includes([
        OpencareConstants.billingStates.BILLED,
        OpencareConstants.billingStates.REFUNDED,
        OpencareConstants.billingStates.WRITE_OFF,
      ], billingStatus);
    },

    isDeferred: function(appointment) {
      return _.some(appointment.history, function(historyItem) {
        return historyItem.actionType == actionTypes.BILLING_DEFER;
      });
    },

    isFromRecommendations: function(appointment) {
      return _.get(appointment, 'requestData.source') == 'recommendations';
    },

    isUSAppointment: function(appointment) {
      return OCStringUtil.isUnitedStates(_.get(appointment, 'provider.clinic.address.country'));
    },

    isVerificationHistoryItem: (historyItem) => typeof historyItem.metadata?.appointmentVerified === 'boolean',

    isStatusChangeHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.STATUS;
    },

    isMessageToPatientHistoryItem: function(historyItem) {
      return historyItem.actor == OpencareConstants.appointmentHistory.actors.provider
        && historyItem.actionType == actionTypes.MESSAGE;
    },

    isMessageFromPatientHistoryItem: function(historyItem) {
      return historyItem.actor == OpencareConstants.appointmentHistory.actors.patient
        && historyItem.actionType == actionTypes.MESSAGE;
    },

    isStaffNoteHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.STAFF_NOTE;
    },

    isPhoneCallHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.PHONE_CALL;
    },

    isReconfirmationFlowHistoryItem: function(historyItem) {
      return _.includes([
        actionTypes.RECONFIRMATION_SMS,
        actionTypes.RECONFIRMATION_NOT_SENT,
        actionTypes.RECONFIRMATION_NEGATIVE_RESPONSE,
        actionTypes.RECONFIRMATION_POSITIVE_RESPONSE,
        actionTypes.RECONFIRMATION_LATE_RESPONSE,
        actionTypes.RECONFIRMATION_NO_RESPONSE,
        actionTypes.RECONFIRMATION_NEGATIVE_RESPONSE_FOLLOWUP_RESCHEDULE,
        actionTypes.RECONFIRMATION_NEGATIVE_RESPONSE_FOLLOWUP_CANCEL,
        actionTypes.RECONFIRMATION_NEGATIVE_RESPONSE_AUTO_RESCHEDULE,
      ], historyItem.actionType);
    },

    isReconfirmationSmsHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.RECONFIRMATION_SMS;
    },

    isReconfirmationNotSentHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.RECONFIRMATION_NOT_SENT;
    },

    isReconfirmationNegativeResponseHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.RECONFIRMATION_NEGATIVE_RESPONSE;
    },

    isReconfirmationPositiveResponseHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.RECONFIRMATION_POSITIVE_RESPONSE;
    },

    isReconfirmationLateResponseHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.RECONFIRMATION_LATE_RESPONSE;
    },

    isReconfirmationNoResponseHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.RECONFIRMATION_NO_RESPONSE;
    },

    isReconfirmationNegativeResponseFollowupRescheduleHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.RECONFIRMATION_NEGATIVE_RESPONSE_FOLLOWUP_RESCHEDULE;
    },

    isReconfirmationNegativeResponseFollowupCancelHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.RECONFIRMATION_NEGATIVE_RESPONSE_FOLLOWUP_CANCEL;
    },

    isReconfirmationNegativeResponseAutoRescheduleHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.RECONFIRMATION_NEGATIVE_RESPONSE_AUTO_RESCHEDULE;
    },

    isOffHoursSmsHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.OFF_HOURS_SMS;
    },

    isAutoCancellationSmsHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.AUTO_CANCELLATION_SMS;
    },

    isNprDripSmsHistoryItem: function(historyItem) {
      return historyItem.actionType == actionTypes.NPR_DRIP_V2_SMS;
    },

    isAutomatedHistoryItem: function(historyItem) {
      return self.isReconfirmationFlowHistoryItem(historyItem)
        || self.isOffHoursSmsHistoryItem(historyItem)
        || self.isAutoCancellationSmsHistoryItem(historyItem)
        || self.isNprDripSmsHistoryItem(historyItem);
    },

    isActionableReconfirmationFlowHistoryItem: function(historyItem) {
      return self.isReconfirmationNegativeResponseFollowupRescheduleHistoryItem(historyItem)
        || self.isReconfirmationNegativeResponseAutoRescheduleHistoryItem(historyItem)
        || self.isReconfirmationNoResponseHistoryItem(historyItem);
    },

    getLastNotifiableHistoryItem: function(appointment) {
      var historyItemsByDate = _.sortBy(appointment.history, 'createdAt');
      return _.chain(historyItemsByDate).reject(function(historyItem) {
        return self.isStaffNoteHistoryItem(historyItem)
          || (self.isAutomatedHistoryItem(historyItem) && !self.isActionableReconfirmationFlowHistoryItem(historyItem));
      })
        .last()
        .value() || {};
    },

    isPatientDobDefined: function(appointment) {
      var dateOfBirth = _.get(appointment, 'patient.dateOfBirth', null);
      return !!dateOfBirth && moment.utc(dateOfBirth).isAfter('1900-01-01');
    },

    isPatientGenderDefined: function(appointment) {
      var gender = _.get(appointment, 'patient.gender', null);
      return !!gender && gender != 'O';
    },

    isNoShow: (appointment) => Boolean(appointment.verified && !appointment.visited),

    getAppointmentDate: function(appointment) {
      if (appointment.date && _.isNumber(appointment.startTime)) {
        return moment.utc(appointment.date).startOf('day')
          .add(appointment.startTime, 'minutes');
      }
    },

    getSlotDate: function(appointment) {
      if (!appointment.reservedAppointmentSlot) {
        return null;
      }

      return moment.utc(appointment.reservedAppointmentSlot.date).startOf('day')
        .add(appointment.reservedAppointmentSlot.time, 'minutes');
    },

    getPatientName: function(appointment) {
      return [appointment.patient.firstName || '', appointment.patient.lastName || ''].join(' ').trim();
    },

    isScheduledAppointmentRespondable: function(appointment) {
      var lastHistoryItem = self.getLastNotifiableHistoryItem(appointment);
      return self.isMessageFromPatientHistoryItem(lastHistoryItem)
        || self.isActionableReconfirmationFlowHistoryItem(lastHistoryItem);
    },

    isForwardPhoneCallEnabled: function(appointment) {
      return !!(_.get(appointment, 'provider.clinic.forwardPhone')
        && _.get(appointment, 'provider.clinic.phone')
        && _.get(appointment, 'provider.clinic.phoneVerified'));
    },

    isDentalAppointment: function(appointment) {
      return _.get(appointment, 'offeredAppointmentType.appointmentType.specialty') == OpencareConstants.specialtyId.DENTIST;
    },

    /**
     * Determines if an appointment is deferrable based on various conditions, including its status, billing status,
     * invoice status, and the current time relative to automated billing processes.
     *
     * @param {Object} appointment - The appointment object to be checked.
     * @returns {boolean} - Returns true if the appointment is deferrable, false otherwise.
     *
     * The function performs a series of checks:
     * 1. Ensures the appointment is requested, billable, not already deferred, and has a linked invoice.
     * 2. Checks if the invoice is not billed and if the invoice period is in the past compared to the current month.
     * 3. Ensures deferral is not allowed if within an hour of the automated billing process starting for invoices
     *    associated with the previous month.
     */
    isDeferrable: (appointment) => {
      const isRequested = self.isRequested(appointment.status);
      const isBillable = appointment.metadata?.billingStatus === OpencareConstants.billingStates.TO_BILL;
      const isNotDeferred = !self.isDeferred(appointment);
      const hasInvoice = !!appointment.primeInvoice?.id;

      // Base check.
      if (isRequested && isBillable && isNotDeferred && hasInvoice) {
        const currentTime = moment();
        const currentMonth = currentTime.clone().startOf('month');

        // Invoice check.
        const invoiceIsNotBilled = appointment.primeInvoice.billingStatus
          === OpencareConstants.billingStates.NOT_BILLED;
        const invoiceMonth = currentMonth.clone().year(appointment.primeInvoice.year)
          .month(appointment.primeInvoice.month - 1);
        const invoiceIsInPast = invoiceMonth.isBefore(currentMonth);

        if (invoiceIsNotBilled && invoiceIsInPast) {
          // Block deferring for appointments from the previous month
          // 1 hour before the automated billing starts running.
          const automatedBillingStart = moment.tz(OpencareConstants.automatedBilling.timezone)
            .startOf('month')
            .date(OpencareConstants.automatedBilling.dayStart)
            .hour(OpencareConstants.automatedBilling.hourStart);
          const automatedBillingStarted = currentTime.isAfter(automatedBillingStart.subtract(1, 'hour'));
          const invoiceIsFromPrevMonth = currentMonth.clone().subtract(1, 'month')
            .isSame(invoiceMonth, 'month');

          if (!invoiceIsFromPrevMonth || !automatedBillingStarted) {
            return true;
          }
        }
      }

      return false;
    },

    getPatientAvailabilityDisplay: function(appointment) {
      var overallAvailability = _.get(appointment, 'requestData.availability.overall') || _.get(appointment, 'match.other.availability');
      if (!overallAvailability) {
        return;
      }

      var availabilityIds = {
        NO_PREFERENCE: 'nopreference',
        ASAP: 'emergency',
        SPECIFY: 'specify',
      };

      switch (overallAvailability) {
        case availabilityIds.NO_PREFERENCE:
          return 'No Preference';
        case availabilityIds.ASAP:
          return 'ASAP';
        case availabilityIds.SPECIFY:
          var specificAvailabilities = _.get(appointment, 'requestData.availability.specific') || _.get(appointment, 'match.other.specificAvailabilities');
          specificAvailabilities = _.map(specificAvailabilities, function(availability) {
            var availabilityFormatted = availability;
            if (_.includes(['weekend', 'morning', 'afternoon'], availability)) {
              availabilityFormatted += 's';
            }
            var timeRange = AvailabilityConstants.patientAvailabilityTimeRanges[availability];
            if (timeRange) {
              availabilityFormatted += ' (' + timeRange + ')';
            }
            return _.capitalize(availabilityFormatted);
          });
          if (!specificAvailabilities.length) {
            return 'Not specified';
          } else if (specificAvailabilities.length == 1) {
            return specificAvailabilities[0];
          } else {
            return _.take(specificAvailabilities, specificAvailabilities.length - 1).join(', ') + ' or ' + _.last(specificAvailabilities);
          }
      }
    },

    getPatientReasonDisplay: function(appointment, includeOther, includeGiven) {
      var reason = _.get(appointment, 'requestData.reason') || _.get(appointment, 'match.other.reasonRaw');
      var overallReasonId = _.get(reason, 'overall.id');
      var overallReason = _.get(reason, 'overall.name');
      var givenReason = _.get(reason, 'given');
      var otherReason = _.get(reason, 'other');
      var subReasons = _.get(reason, 'subReasons');

      var reasonIds = {
        CHECKUP: 'checkup',
        SERVICES: 'services',
        URGENT: 'urgent',
      };

      switch (overallReasonId) {
        case reasonIds.CHECKUP:
          return overallReason;
        case reasonIds.SERVICES:
          if (_.isEmpty(subReasons)) {
            return overallReason;
          } else {
            return _.chain(subReasons).map(function(subReason) {
              return subReason == 'Other' && includeOther ? otherReason : subReason;
            })
              .compact()
              .value()
              .join(', ');
          }
        case reasonIds.URGENT:
          subReasons = _.chain(subReasons).map(function(subReason) {
            return subReason == 'Other' ? null : subReason;
          })
            .compact()
            .value();
          return overallReason + (!_.isEmpty(subReasons) ? ' - ' + subReasons.join(', ') : '');
        default:
          return includeGiven ? givenReason : overallReason;
      }
    },

    getMemberId: function(appointment) {
      return _.get(appointment, 'insuranceData.memberId')
        || _.get(appointment, 'requestData.match.insuranceMember');
    },

    showSubscriberStatus: function(appointment) {
      return _.isBoolean(_.get(appointment, 'insuranceData.primaryInsuranceMember'))
        || _.isBoolean(_.get(appointment, 'patient.metadata.primaryInsuranceMember'));
    },

    showSubscriberInfo: function(appointment) {
      return self.showSubscriberStatus(appointment) && !self.getPrimaryInsuranceMember(appointment);
    },

    getPrimaryInsuranceMember: function(appointment) {
      var insuranceDataValue = _.get(appointment, 'insuranceData.primaryInsuranceMember');
      if (_.isBoolean(insuranceDataValue)) {
        return insuranceDataValue;
      }

      var patientMetadataValue = _.get(appointment, 'patient.metadata.primaryInsuranceMember');
      if (_.isBoolean(patientMetadataValue)) {
        return patientMetadataValue;
      }
    },

    getSubscriber: function(appointment) {
      var insuranceDataValue = _.get(appointment, 'insuranceData.subscriber');
      if (insuranceDataValue) {
        return insuranceDataValue;
      }

      var patientMetadataFirstName = _.get(appointment, 'patient.metadata.memberFirstName');
      if (patientMetadataFirstName) {
        return {
          firstName: patientMetadataFirstName,
          lastName: _.get(appointment, 'patient.metadata.memberLastName'),
          dob: _.get(appointment, 'patient.metadata.memberDateOfBirth'),
        };
      }
    },

    getFirstMessageTemplate: function(appointment) {
      var messageTemplateFeature = _.get(appointment, 'provider.clinic.metadata.appointmentMessageTemplateFeature');
      var alreadyMessagedPatient = _.some(appointment.history, function(historyItem) {
        return self.isMessageToPatientHistoryItem(historyItem);
      });
      if (
        messageTemplateFeature === false
        || !self.isRequested(appointment.status)
        || !self.isDentalAppointment(appointment)
        || alreadyMessagedPatient
      ) {
        return;
      }

      var patientFirstName = _.get(appointment, 'patient.firstName', 'there');
      var doctorName = _.get(appointment, 'provider.doctor.name');
      var askAboutInsurance = self.isUSAppointment(appointment)
        && _.includes(['Yes', 'Unsure'], _.get(appointment, 'match.other.hasInsurance'))
        && !self.getMemberId(appointment);
      var hasReservedAppointmentSlot = !!appointment.reservedAppointmentSlot;

      var message = [
        'Hi ' + patientFirstName + ',',
        '\n\n',
      ];

      if (hasReservedAppointmentSlot) {
        message = message.concat([
          'Thank you for trusting us with your oral health.',
          '\n\n',
        ]);

        if (askAboutInsurance) {
          message = message.concat([
            "We're currently holding your requested time with " + doctorName + '.',
            ' Please send us your insurance plan member ID, group ID (if your plan includes one) and your date of birth',
            ' so I can assist you with any insurance eligibility questions you may have prior to your appointment.',
          ]);
        } else {
          message = message.concat([
            'Your appointment has been confirmed - Please reply to this message if you have any questions prior to coming in to our office.',
          ]);
        }

        message = message.concat([
          '\n\n',
          'We look forward to seeing you soon!',
        ]);
      } else {
        message = message.concat([
          'Just following up on your appointment request. Thank you for trusting us with your oral health!',
          " We're sending some times that we have available based on your preferences.",
          ' Please respond ASAP as these times may fill up.',
        ]);

        if (askAboutInsurance) {
          message = message.concat([
            " Once you've picked a time, please send us your insurance member ID or SSN to help us double-check your coverage before your appointment.",
          ]);
        }

        message = message.concat([
          '\n\n',
          'We look forward to hearing from you!',
        ]);

      }

      return {
        message: message.join(''),
        type: hasReservedAppointmentSlot ? 'reservedSlotConfirmation' : 'requestFollowup',
      };
    },

    isMemberIdPending: function(appointment) {
      var reminderActive = _.get(appointment, 'insuranceData.metadata.memberIdReminder.active');
      var reminderSent = _.get(appointment, 'insuranceData.metadata.memberIdReminder.sent');
      var hasMessage = _.some(appointment.history, function(item) {
        return self.isMessageToPatientHistoryItem(item) || self.isMessageFromPatientHistoryItem(item);
      });
      var hasMemberId = self.getMemberId(appointment);
      return reminderActive && !reminderSent && !hasMessage && !hasMemberId;
    },

    getInNetwork: function(appointment) {
      var isUSAppointment = self.isUSAppointment(appointment);
      var provider = _.get(appointment, 'insuranceData.provider');
      var plan = _.get(appointment, 'insuranceData.providerPlan');
      if (!provider || !plan || !isUSAppointment) {
        return $q.resolve(null);
      }

      return DoctorService.getInNetwork(appointment.provider.doctor.id, plan, provider).then(function(result) {
        return result.data.inNetwork;
      });
    },

    /* This is taken directly from the superpractice query. Update if that query changes
      ("actionType" = 'STATUS' AND "toStatus" = 'SCHEDULED')
      OR
      (
        actor = 'provider' AND
          "actionType" IN
          (
            'PHONE_CALL',
            'STAFF_NOTE',
            'PROVIDER_ADDED_RESCHEDULE_SLOTS',
            'MESSAGE'
          )
          AND "type" != 'AUTOMATED_MESSAGE'
      )
    */
    getFirstResponseFromClinic: (appointment) =>
      appointment.history.find((aH) => {
        if (aH.actionType === 'STATUS' && self.isScheduled(aH.toStatus)) {
          return true;
        }
        if (aH.actor === 'provider') {
          if (['PHONE_CALL', 'STAFF_NOTE', 'PROVIDER_ADDED_RESCHEDULE_SLOTS', 'MESSAGE'].includes(aH.actionType)) {
            if (aH.type !== 'AUTOMATED_MESSAGE') {
              return true;
            }
          }
        }
        return false;
      }),

    hasClinicSentAnyMessages: (appointment) => {
      return appointment.history.some((h) => {
        if (h.actor === OpencareConstants.appointmentHistory.actors.provider) {
          if (h.actionType === actionTypes.MESSAGE) {
            return true;
          }
          if (h.actionType === actionTypes.STATUS
            && h.toStatus === OpencareConstants.appointmentStates.SCHEDULED) {
            return true;
          }
        }
        return false;
      });
    },

    hasAnyNewMessages: (appointment) => {
      return appointment.history.some(
        (h) =>
          h.actionType === actionTypes.MESSAGE
          && h.actor === OpencareConstants.appointmentHistory.actors.patient
          && !h.viewedByClinicAt,
      );
    },

    hasUnviewedStatusChanges: (appointment) => {
      const lastHistoryItem = self.getLastNotifiableHistoryItem(
        appointment,
      );
      return (
        lastHistoryItem.actor === OpencareConstants.appointmentHistory.actors.patient
        && lastHistoryItem.actionType === actionTypes.STATUS
        && lastHistoryItem.toStatus === OpencareConstants.appointmentStates.REQUESTED
        && !lastHistoryItem.viewedByClinicAt
      );
    },
  };

  return self;
});
